import { useCallback, useEffect, useMemo, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  GrcButton,
  GrcButtonStylingMode,
  GrcButtonType,
  GrcGrid,
  gridOptions,
  DomLayout
} from '@grc/ui-package';
import { QuoteTabKey } from '../../../models/quotes/quote-tab.interface';
import { IDateRangeFilterValue } from '../../../common/date-range-filter/date-range-filter.interface';
import { useI18nState } from '../../../stores/settings/language-context';
import { useQuoteGridData } from '../useQuoteGridData';
import { IQuoteGridRow } from '../../../models/quote-management/quote-grid.interface';
import { useQuoteSettings } from '../useQuoteSettings';
import { ApprovalDecisionSource } from '../useApprovalDecision';
import { getQuoteGridColumns } from './quote-grid.columns';
import './quote-grid.scss';

interface IQuoteGridProps {
  tab: QuoteTabKey;
  searchText: string;
  dateFilter: IDateRangeFilterValue;
  refreshToken: number;
  selectedCount: number;
  onApprove: (quoteIds: string[], source: ApprovalDecisionSource) => void;
  onReject: (quoteIds: string[], source: ApprovalDecisionSource) => void;
  onSelectionChange: (quoteIds: string[]) => void;
  onBulkApprove: () => void;
  onBulkReject: () => void;
}

const getQuoteRowId = ({ data }: { data: IQuoteGridRow }) => data.id;

export default function QuoteGrid({
  tab,
  searchText,
  dateFilter,
  refreshToken,
  selectedCount,
  onApprove,
  onReject,
  onSelectionChange,
  onBulkApprove,
  onBulkReject
}: IQuoteGridProps) {
  const gridRef = useRef<AgGridReact<IQuoteGridRow>>(null);
  const isFirstRefresh = useRef(true);
  const { localeData } = useI18nState();
  const { serverPaging } = useQuoteGridData(
    tab,
    searchText,
    dateFilter,
    refreshToken
  );
  // Mode and HU / Product names fill in once the settings load (colDefs change)
  const { transportationModes, handlingUnitTypes } = useQuoteSettings();

  const isPendingForApproval = tab === 'pendingForApproval';
  const source: ApprovalDecisionSource = tab === 'quoteRequests'
    ? 'quoteRequests'
    : 'pendingForApproval';

  // TBD - no edit-quote route/flow is confirmed yet (plan doesn't define one
  // for the Quote Requests tab). Flagged rather than fabricated, same
  // "documented TBD" precedent as shipment-grid.columns.tsx's TransportID
  // column.
  const handleEdit = useCallback((_quoteId: string) => {}, []);

  const handleApprove = useCallback(
    (quoteId: string) => onApprove([quoteId], source),
    [onApprove, source]
  );

  const handleReject = useCallback(
    (quoteId: string) => onReject([quoteId], source),
    [onReject, source]
  );

  // Server-side row model: getSelectedRows() only returns rows the grid has
  // loaded, so bulk approve/reject acts on the selected rows of loaded pages.
  const handleSelectionChanged = useCallback(() => {
    const selectedIds =
      gridRef.current?.api
        ?.getSelectedRows()
        .map((row: IQuoteGridRow) => row.id) ?? [];
    onSelectionChange(selectedIds);
  }, [onSelectionChange]);

  const colDefs = useMemo(
    () =>
      getQuoteGridColumns({
        localeData,
        tab,
        transportationModes,
        handlingUnitTypes,
        onEdit: handleEdit,
        onApprove: handleApprove,
        onReject: handleReject
      }),
    [
      localeData,
      tab,
      transportationModes,
      handlingUnitTypes,
      handleEdit,
      handleApprove,
      handleReject
    ]
  );

  // getRowId keeps selection tied to the quote across server-side reloads
  const otherProps = useMemo(
    () => ({
      getRowId: getQuoteRowId,
      ...(isPendingForApproval
        ? { onSelectionChanged: handleSelectionChanged }
        : {})
    }),
    [isPendingForApproval, handleSelectionChanged]
  );

  // A reload (tab filters, search, or after approve/reject) replaces the rows,
  // so drop the old selection with them.
  useEffect(() => {
    if (isFirstRefresh.current) {
      isFirstRefresh.current = false;
      return;
    }
    if (!isPendingForApproval) return;
    gridRef.current?.api?.deselectAll();
    onSelectionChange([]);
  }, [serverPaging.refreshKey, isPendingForApproval, onSelectionChange]);

  return (
    <div className="quote-grid-wrapper" data-testid="quote_grid_wrapper">
      {isPendingForApproval && (
        <div
          className="quote-grid-bulk-actions d-flex justify-content-end"
          data-testid="quote_grid_bulk_actions"
        >
          <GrcButton
            id="quote_grid_bulk_reject_button"
            text={localeData.Reject}
            type={GrcButtonType.Danger}
            stylingMode={GrcButtonStylingMode.Outlined}
            disabled={selectedCount === 0}
            onClick={onBulkReject}
          />
          <GrcButton
            id="quote_grid_bulk_approve_button"
            text={localeData.Approve}
            type={GrcButtonType.Success}
            stylingMode={GrcButtonStylingMode.Contained}
            disabled={selectedCount === 0}
            onClick={onBulkApprove}
          />
        </div>
      )}
      <div
        className="quote-grid-container grc-common-ag-grid"
        data-testid="quote_grid"
      >
        <GrcGrid<any>
          colDefs={colDefs}
          gridRef={gridRef}
          gridOptions={gridOptions}
          otherProps={otherProps}
          serverPaging={serverPaging}
          domLayout={DomLayout.Normal}
        />
      </div>
    </div>
  );
}
